<script setup lang="ts">
import { ref, onMounted, computed } from 'vue'
import firebase from "@/firebase"
import VueMultiselect from 'vue-multiselect'
import type { AICollabMember } from "@/types"
import { assignProjectToUser } from "@/composables/aiChatFunctions"
import { useStore } from 'vuex'

const store = useStore()
const selectedProject = computed(() => store.getters['ai/selectedProject'])

const isLoadingUsers = ref(false)
const users = ref<AICollabMember[]>([])
const selectedUsers = ref<AICollabMember[]>([])

const getUsers = async () => {
  isLoadingUsers.value = true
  const db = firebase.firestore().collection("team")
  const snapshot = await db.get()
  
  const newUsers = []
  snapshot.docs.forEach((doc) => {
    const firstName = doc.data().name.first
    const lastName = doc.data().name.last
    newUsers.push({
      id: doc.id,
      firstName: firstName,
      lastName: lastName,
      fullName: `${firstName} ${lastName}`
    })
  })
  
  users.value = newUsers
  isLoadingUsers.value = false
}

const nonAddedUsers = computed(() => {
  return users.value.filter((user) => {
    return !selectedProject.value.members.some((member) => member.id === user.id)
  })
})

const removeMember = async (member: AICollabMember) => {
  try {
    await store.dispatch('ai/removeProjectMember', member)
  } catch (error) {
    console.error('Error removing member:', error)
  }
}

const addMembers = async () => {
  try {
    const newMembers = selectedUsers.value.map(user => ({
      id: user.id,
      name: user.fullName
    }))
    
    await store.dispatch('ai/addProjectMembers', newMembers)
    selectedUsers.value = []

    // Assign project to each new user
    newMembers.forEach(async (member) => {
      await assignProjectToUser(selectedProject.value.id, member.id)
    })
    
  } catch (error) {
    console.error('Error adding members:', error)
  }
}

onMounted(async () => {
  await getUsers()
})
</script>

<template>
  <div class="manage-members-container">
    <div class="current-members my-12">
      <h3 class="text-2xl font-semibold mb-6">Mevcut Üyeler</h3>
      <div class="member-list text-2xl space-y-2">
        <div v-for="member in selectedProject.members" :key="member.id" class="member-item flex items-center justify-between p-2 bg-gray-50 rounded">
          <span>{{ member.name }}</span>
          <button 
            @click="removeMember(member)"
            class="text-red-500 text-2xl hover:text-red-700"
          >
            <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6" viewBox="0 0 20 20" fill="currentColor">
              <path fill-rule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z" clip-rule="evenodd" />
            </svg>
          </button>
        </div>
      </div>
    </div>

    <div class="add-members">
      <h3 class="text-2xl font-semibold mt-12 mb-4">Yeni Üye Ekle</h3>
      <div class="flex gap-4">
        <VueMultiselect
          v-model="selectedUsers"
          :options="nonAddedUsers"
          :multiple="true"
          :close-on-select="false"
          :clear-on-select="false"
          :preserve-search="true"
          placeholder="Kullanıcıları Seçin"
          label="fullName"
          track-by="id"
          :loading="isLoadingUsers"
          class="flex-1"
        />
        <button
          @click="addMembers"
          class="px-4 py-2 bg-blue-500 cursor-pointer text-white rounded text-2xl hover:bg-blue-600"
          :disabled="!selectedUsers.length"
        >
          Ekle
        </button>
      </div>
    </div>
  </div>
</template>

<style src="vue-multiselect/dist/vue-multiselect.css"></style>
